import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { createChannel, finishConnect, startConnect } from './alert-routing-api'
import { peekPending, takePending } from './connect-pending'
import { providerLabel, useLeaveForConsent } from './connection-field'
import { CONNECTIONS_PAGE, TARGETS_PAGE } from './paths'
import { channelNameKey } from './schema-form'

// "Connected to Acme (connected by Dana Ops, just now)."
export const connectedFlash = connection => {
  const by = connection.connectedBy && connection.connectedBy.name
  return `Connected to ${connection.externalName}${by ? ` (connected by ${by}, just now)` : ' (just now)'}.`
}

// What the provider's consent page sent back, read once when the page opens.
const arrivalOf = search => {
  const q = new URLSearchParams(search)
  const error = q.get('error')
  const code = q.get('code')
  const state = q.get('state')
  if (error === 'access_denied') return { kind: 'cancelled' }
  if (error) return { kind: 'failed', provider: true }
  if (!code || !state) return { kind: 'nothing' }
  return { kind: 'working', code, state }
}

// /admin/alert-routing/connect/:provider, where the provider's consent page
// sends the person back (auth's redirect URI). Its code and state are
// credentials: the address bar loses them before anything is sent, so they
// never stay in the history, a bookmark or a screenshot. Then finish
// (POST /connect/:provider/finish, the two in its body), take what the
// person was doing (connect-pending.js) and go back there with a flash and
// their draft, its workspace picked; a channel that waited for the new
// permission (+ New channel) is created once and picked. Cancel at the
// provider, or a refusal, stays here and says so: Connect again starts
// over, Back goes back with the draft (to the target being made, else the
// connections page, where a workspace with nothing pending comes back to).
export const ConnectReturn = () => {
  const { provider } = useParams()
  const location = useLocation()
  const history = useHistory()
  const label = providerLabel(provider)
  const [arrival] = useState(() => arrivalOf(location.search))
  const [phase, setPhase] = useState(() => (arrival.kind === 'failed'
    ? { kind: 'failed', message: `${label} didn't finish connecting. Choose Connect again to try once more.` }
    : { kind: arrival.kind }))
  const consent = useLeaveForConsent()
  const started = useRef(false)
  const mounted = useRef(false)
  const stripped = !location.search

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  // First: the code and state leave the address bar.
  useEffect(() => {
    if (!stripped) history.replace(location.pathname)
  }, [stripped, history, location.pathname])

  // Only then, once: finish.
  useEffect(() => {
    if (!stripped || started.current || arrival.kind !== 'working') return
    started.current = true
    const run = async () => {
      let out
      try {
        out = await finishConnect(provider, { code: arrival.code, state: arrival.state })
      } catch (err) {
        if (mounted.current) setPhase({ kind: 'failed', message: serverMessage(err, `${label} couldn't be connected.`), failure: err })
        return
      }
      const kept = takePending()
      const mine = kept && kept.provider === provider ? kept : null
      const connection = out.connection
      let flash = connectedFlash(connection)
      let problem = null
      const draft = mine && mine.draft ? { ...mine.draft, config: { ...(mine.draft.config || {}) } } : null
      if (draft && draft.connectionKey) draft.config[draft.connectionKey] = connection.id
      const wanted = mine && mine.newChannel
      if (wanted && wanted.name) {
        try {
          const made = await createChannel(connection.id, { name: wanted.name, private: Boolean(wanted.private) })
          flash = `${flash} Created #${made.channel.name}.`
          if (draft && wanted.key) {
            draft.config[wanted.key] = made.channel.id
            draft.config[channelNameKey(wanted.key)] = made.channel.name
          }
        } catch (err) {
          problem = `#${wanted.name} wasn't created: ${serverMessage(err, `${label} refused it.`)}`
        }
      }
      if (!mounted.current) return
      history.replace((mine && mine.returnTo) || CONNECTIONS_PAGE, { flash, ...(draft ? { draft } : {}), ...(problem ? { problem } : {}) })
    }
    run()
  }, [stripped, arrival, provider, label, history])

  const back = () => {
    const kept = takePending()
    history.replace((kept && kept.returnTo) || CONNECTIONS_PAGE, kept && kept.draft ? { draft: kept.draft } : undefined)
  }
  const again = () => {
    const kept = peekPending()
    consent.leave(() => startConnect(provider, { scope: 'platform' }),
      { provider, purpose: 'connect', returnTo: kept && kept.returnTo, draft: kept && kept.draft, newChannel: kept && kept.newChannel },
      `${label} couldn't be reached. Try again.`)
  }
  // Back where the person came from: the target they were making, else the
  // connections page.
  const waiting = peekPending()
  const toTarget = Boolean(waiting && waiting.returnTo && waiting.returnTo.startsWith(TARGETS_PAGE))
  const backButton = <button type="button" className="btn sw-btn" onClick={back}>{toTarget ? 'Back to the target' : 'Back to connections'}</button>

  return (
    <FleetPage title={`Connect ${label}`}>
      {phase.kind === 'working' && <p className="sw-hint" role="status">{`Finishing the connection with ${label}…`}</p>}
      {phase.kind === 'cancelled' &&
        <>
          <Notice tone="info">{`You chose Cancel at ${label}, so nothing changed.`}</Notice>
          <div className="ar-row">{backButton}</div>
        </>}
      {phase.kind === 'nothing' &&
        <>
          <Notice tone="info"><span>There's nothing to finish here.</span> {`To connect ${label}, go to Alert connections and choose Connect ${label}.`}</Notice>
          <div className="ar-row">{backButton}</div>
        </>}
      {phase.kind === 'failed' &&
        <>
          <Notice tone="bad" role="alert">{phase.message}<ErrorReference error={phase.failure} /></Notice>
          {consent.error && <Notice tone="bad" role="alert">{consent.error}<ErrorReference error={consent.failure} /></Notice>}
          <div className="ar-row">
            <button type="button" className="btn sw-btn-primary" disabled={consent.busy} onClick={again}>Connect again</button>
            {backButton}
          </div>
        </>}
    </FleetPage>
  )
}
